import { useMemo, useState } from 'react'
import { DialogTitle } from '@headlessui/react'
import SimpleModal from '../../../components/SimpleModal'
import LevelPill from '../../../components/badges/LevelPill'
import { Level } from '../types'

interface Props {
    open: boolean
    onClose: () => void
    classifierName: string
}

type Scope = 'All' | 'Profile' | 'Team' | 'Organisation'
const SCOPES: Scope[] = ['All', 'Profile', 'Team', 'Organisation']
const SCOPE_LEVEL: Record<Exclude<Scope, 'All'>, Level> = {
    Profile: 'profile',
    Team: 'team',
    Organisation: 'org'
}

// Candidates not yet running this classifier — profiles, teams and organisations.
// Applications land on profiles only: picking a team or org is a bulk-add to each
// of its current profiles, not a standing link. addedRank orders them by how
// recently they were onboarded (1 = most recent) so Service Delivery sees newly
// added profiles first and can configure them quickly.
const CANDIDATES: { id: string; name: string; level: Level; context: string; addedRank: number }[] = [
    { id: 'p-acme-shop-fb', name: '@AcmeShop · Facebook', level: 'profile', context: 'Acme — Support · Acme Corp', addedRank: 1 },
    { id: 'p-acme-brand-ig', name: '@AcmeBrand · Instagram', level: 'profile', context: 'Acme — Brand · Acme Corp', addedRank: 2 },
    { id: 'p-soylent-yt', name: '@SoylentMedia · YouTube', level: 'profile', context: 'Soylent — Social · Soylent Media', addedRank: 3 },
    { id: 't-globex-comms', name: 'Globex — Comms', level: 'team', context: 'Globex · 40 profiles', addedRank: 4 },
    { id: 't-stark-comms', name: 'Stark — Comms', level: 'team', context: 'Stark Industries · 28 profiles', addedRank: 5 },
    { id: 'o-soylent', name: 'Soylent Media', level: 'org', context: '4 teams · 120 profiles', addedRank: 6 },
    { id: 'o-umbrella', name: 'Umbrella Co', level: 'org', context: '1 team · 12 profiles', addedRank: 7 }
]

export default function AddProfilesModal({ open, onClose, classifierName }: Props) {
    const [query, setQuery] = useState('')
    const [scope, setScope] = useState<Scope>('All')
    const [selected, setSelected] = useState<Set<string>>(new Set())

    const results = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return [...CANDIDATES].sort((a, b) => a.addedRank - b.addedRank)
        return CANDIDATES.filter((c) => {
            if (scope !== 'All' && c.level !== SCOPE_LEVEL[scope]) return false
            return c.name.toLowerCase().includes(q) || c.context.toLowerCase().includes(q)
        }).sort((a, b) => a.addedRank - b.addedRank)
    }, [query, scope])

    const toggle = (id: string) =>
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })

    const close = () => {
        // Reset transient selection so the modal reopens clean.
        setQuery('')
        setScope('All')
        setSelected(new Set())
        onClose()
    }

    return (
        <SimpleModal open={open} onClose={close}>
            <div className="w-[560px] max-w-[94vw] rounded-xl bg-white shadow-2xl text-sm text-gray-800 text-left overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100">
                    <DialogTitle as="h3" className="font-sans !font-medium text-lg text-gray-900 m-0">
                        Add to &ldquo;{classifierName}&rdquo;
                    </DialogTitle>
                    <p className="text-gray-700 text-[12.5px] mt-1 mb-0">
                        Search by profile, team or organisation. Adding a team or organisation
                        applies the classifier to each of its current profiles — a one-time
                        application, not a standing link.
                    </p>
                </div>

                {/* Search + scope */}
                <div className="px-5 pt-4 pb-3 border-b border-gray-100">
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search profiles, teams or organisations…"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-arwen-300"
                    />
                    <div className="mt-2.5 inline-flex overflow-hidden rounded-lg border border-gray-200" role="group" aria-label="Search scope">
                        {SCOPES.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setScope(s)}
                                className={`px-3 py-1.5 text-xs font-semibold border-l border-gray-200 first:border-l-0 ${
                                    scope === s ? 'bg-arwen-600 text-white' : 'bg-white text-gray-800 hover:bg-gray-50'
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Results */}
                <div className="max-h-[320px] overflow-y-auto px-2 py-2">
                    {results.length === 0 ? (
                        <div className="px-3 py-8 text-center text-xs text-gray-800">
                            No profiles match &ldquo;{query}&rdquo;.
                        </div>
                    ) : (
                        results.map((c) => {
                            const isChecked = selected.has(c.id)
                            return (
                                <label
                                    key={c.id}
                                    className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 ${
                                        isChecked ? 'bg-arwen-50' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggle(c.id)}
                                        className="h-4 w-4 flex-none accent-arwen-600"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-900">{c.name}</span>
                                            <LevelPill level={c.level} />
                                        </div>
                                        <div className="text-[11.5px] text-gray-800">{c.context}</div>
                                    </div>
                                </label>
                            )
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <span className="text-xs text-gray-800">
                        {selected.size} selected
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={close}
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={close}
                            disabled={selected.size === 0}
                            className="rounded-lg bg-arwen-600 px-4 py-2 text-sm font-bold text-white hover:bg-arwen-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Add{selected.size > 0 ? ` ${selected.size}` : ''}
                        </button>
                    </div>
                </div>
            </div>
        </SimpleModal>
    )
}
